import { useMemo, useState } from "react";
import type { Application } from "../api";
import {
  applicationSearchText,
  applicationTypeLabel,
  formatDate,
  initials,
} from "../presentation";
import { ApplicationStatusBadge } from "./ApplicationStatusBadge";
import { CopyText } from "./ManagementCommon";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";

type StatusFilter = "all" | "active" | "disabled";

interface ApplicationsViewProps {
  readonly applications: readonly Application[];
  readonly onCreate: () => void;
  readonly onSelect: (application: Application) => void;
}

/**
 * Enterprise application inventory: summary metrics, filters and a semantic
 * data table replace the former master/detail card wall.
 */
export function ApplicationsView({ applications, onCreate, onSelect }: ApplicationsViewProps) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<"all" | Application["appType"]>("all");
  const normalizedQuery = query.trim().toLocaleLowerCase();

  const counts = useMemo(() => ({
    total: applications.length,
    active: applications.filter((app) => app.status === "active").length,
    native: applications.filter((app) => app.appType === "native").length,
    attention: applications.filter((app) => app.status === "error" || app.status === "provisioning").length,
  }), [applications]);

  const filtered = useMemo(() => applications.filter((application) => {
    const matchesQuery = normalizedQuery === "" || applicationSearchText(application).includes(normalizedQuery);
    const matchesStatus = statusFilter === "all" || application.status === statusFilter;
    const matchesType = typeFilter === "all" || application.appType === typeFilter;
    return matchesQuery && matchesStatus && matchesType;
  }), [applications, normalizedQuery, statusFilter, typeFilter]);

  return (
    <div className="applications-page">
      <section className="page-heading">
        <div>
          <p className="breadcrumb"><span>开放平台</span><Icon name="chevron-right" size={14} />应用管理</p>
          <h2>应用</h2>
          <p>每个应用拥有独立的 AppKey、租户空间和安全边界。</p>
        </div>
        <Button leadingIcon="plus" variant="primary" onClick={onCreate}>创建应用</Button>
      </section>

      <section className="metric-grid" aria-label="应用概览">
        <MetricCard icon="apps" label="应用总数" note="当前账号可管理" value={counts.total} />
        <MetricCard icon="activity" label="运行中" note="可以正常访问 IM" tone="success" value={counts.active} />
        <MetricCard icon="smartphone" label="原生应用" note="使用 AppKey + UserSig" value={counts.native} />
        <MetricCard icon="key" label="需要关注" note="配置中或同步失败" tone={counts.attention > 0 ? "warning" : "default"} value={counts.attention} />
      </section>

      <section className="data-card">
        <header className="data-card-header">
          <div><h3>全部应用</h3><p>共 {applications.length} 个应用</p></div>
          <div className="table-toolbar">
            <label className="search-control">
              <Icon name="search" size={17} />
              <span className="visually-hidden">搜索应用</span>
              <input value={query} placeholder="搜索名称、AppKey 或 Tenant ID" onChange={(event) => { setQuery(event.currentTarget.value); }} />
            </label>
            <label className="select-control">
              <span className="visually-hidden">按应用类型筛选</span>
              <select value={typeFilter} onChange={(event) => { setTypeFilter(event.currentTarget.value as typeof typeFilter); }}>
                <option value="all">全部类型</option>
                <option value="web">Web 应用</option>
                <option value="native">原生应用</option>
              </select>
              <Icon name="chevron-down" size={15} />
            </label>
          </div>
        </header>
        <div className="filter-tabs" role="tablist" aria-label="按状态筛选">
          <FilterTab active={statusFilter === "all"} count={applications.length} label="全部" onClick={() => { setStatusFilter("all"); }} />
          <FilterTab active={statusFilter === "active"} count={counts.active} label="运行中" onClick={() => { setStatusFilter("active"); }} />
          <FilterTab active={statusFilter === "disabled"} count={applications.filter((app) => app.status === "disabled").length} label="已停用" onClick={() => { setStatusFilter("disabled"); }} />
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon"><Icon name="apps" size={24} /></span>
            <strong>{applications.length === 0 ? "还没有 IM 应用" : "没有符合条件的应用"}</strong>
            <p>{applications.length === 0 ? "创建第一个应用，约一分钟完成基础接入。" : "调整搜索关键词或筛选条件后重试。"}</p>
            {applications.length === 0 ? <Button leadingIcon="plus" variant="primary" onClick={onCreate}>创建应用</Button> : null}
          </div>
        ) : (
          <div className="table-container">
            <table className="application-table">
              <thead><tr><th>应用</th><th>类型</th><th>AppKey / Client ID</th><th>状态</th><th>最后更新</th><th><span className="visually-hidden">操作</span></th></tr></thead>
              <tbody>
                {filtered.map((application) => (
                  <tr key={application.appId}>
                    <td>
                      <button className="application-name" type="button" onClick={() => { onSelect(application); }}>
                        <span className={`application-avatar type-${application.appType}`}>{initials(application.name)}</span>
                        <span><strong>{application.name}</strong><small>{application.description ?? "暂无应用描述"}</small></span>
                      </button>
                    </td>
                    <td><span className="type-label"><Icon name={application.appType === "native" ? "smartphone" : "globe"} size={15} />{applicationTypeLabel(application.appType)}</span></td>
                    <td><CopyText label="AppKey" value={application.appKey} /></td>
                    <td><ApplicationStatusBadge status={application.status} /></td>
                    <td><time dateTime={new Date(application.updatedAt).toISOString()}>{formatDate(application.updatedAt)}</time></td>
                    <td><button aria-label={`查看${application.name}`} className="row-action" type="button" onClick={() => { onSelect(application); }}><Icon name="chevron-right" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <footer className="data-card-footer">显示 {filtered.length} / {applications.length} 个应用</footer>
      </section>
    </div>
  );
}

function MetricCard({ icon, label, note, value, tone = "default" }: {
  readonly icon: "activity" | "apps" | "key" | "smartphone";
  readonly label: string;
  readonly note: string;
  readonly value: number;
  readonly tone?: "default" | "success" | "warning";
}) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <div className="metric-card-top"><span className="metric-icon"><Icon name={icon} /></span><span className="metric-trend">实时</span></div>
      <strong>{value}</strong><p>{label}</p><small>{note}</small>
    </article>
  );
}

function FilterTab({ active, count, label, onClick }: {
  readonly active: boolean;
  readonly count: number;
  readonly label: string;
  readonly onClick: () => void;
}) {
  return <button aria-selected={active} className={active ? "active" : ""} role="tab" type="button" onClick={onClick}>{label}<span>{count}</span></button>;
}
